import { useEffect, useLayoutEffect, useState } from 'react';
import { IS_PRODUCTION } from '../config/buildEnv';
import { isTabletFormFactor } from './mobileView';

const STORAGE_KEY = 'roomnexa.dashboard.pendingTasks.collapsed';
const TABLET_STORAGE_KEY = 'roomnexa.dashboard.pendingTasks.collapsed.tablet';

/** Desktop side rail activates at lg; tablet/iPad always stacks below room board. */
export function getPendingTasksRailSplitBp(stackedLayout = false) {
  return stackedLayout ? null : 'lg';
}

export const PENDING_TASKS_STACKED_MAX_HEIGHT = {
  xs: '40vh',
  sm: '42vh',
};

function getPendingTasksCollapsedStorageKey() {
  return isTabletFormFactor() ? TABLET_STORAGE_KEY : STORAGE_KEY;
}
const TASKS_TAB_OFFSET_PX = 12;
const FALLBACK_TOP_PX = 228;

export const PENDING_TASKS_RAIL_WIDTH = {
  lg: 340,
  xl: 380,
};

export const RAIL_TRANSITION_MS = 560;
export const RAIL_TRANSITION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
export const RAIL_LAYOUT_TRANSITION = `flex-basis ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}, width ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}, max-width ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}, gap ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}`;
export const RAIL_PANEL_TRANSITION = `
  flex-basis ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING},
  width ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING},
  max-width ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING},
  opacity ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING},
  transform ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}
`;
export const PENDING_TASKS_TAB_FLOAT_DURATION_MS = 2800;
export const PENDING_TASKS_TAB_Z_INDEX = 1300;

export function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function usePrefersReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(() => prefersReducedMotion());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    let media;
    try {
      media = window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch {
      return undefined;
    }

    const handleChange = () => setReducedMotion(media.matches);
    handleChange();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }

    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  return reducedMotion;
}

export function getPendingTasksGridColumns(collapsed, width = PENDING_TASKS_RAIL_WIDTH.lg) {
  return collapsed ? 'minmax(0, 1fr) 0px' : `minmax(0, 1fr) ${width}px`;
}

export function getDashboardMainSplitSx({ collapsed, reducedMotion, stackedLayout = false }) {
  const split = getPendingTasksRailSplitBp(stackedLayout);
  const duration = getRailMotionDuration(reducedMotion);

  if (stackedLayout) {
    return {
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      width: '100%',
      gap: collapsed ? 0 : 1.5,
      transition: reducedMotion ? 'none' : `gap ${duration}ms ${RAIL_TRANSITION_EASING}`,
    };
  }

  return {
    flex: 1,
    minHeight: 0,
    overflow: { xs: 'visible', [split]: 'hidden' },
    display: 'flex',
    flexDirection: { xs: 'column', [split]: 'row' },
    alignItems: 'stretch',
    width: '100%',
    gap: { xs: 2, [split]: collapsed ? 0 : 2 },
    transition: reducedMotion ? 'none' : `gap ${duration}ms ${RAIL_TRANSITION_EASING}`,
  };
}

export function getDashboardRoomBoardColumnSx(reducedMotion, stackedLayout = false) {
  const split = getPendingTasksRailSplitBp(stackedLayout);

  if (stackedLayout) {
    return {
      flex: '1 1 0',
      minWidth: 0,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    };
  }

  return {
    flex: '1 1 0',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    height: { [split]: '100%' },
    overflow: 'hidden',
    transition: reducedMotion
      ? 'none'
      : `flex-grow ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}, flex-basis ${RAIL_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}`,
  };
}

export function getPendingTasksRailColumnSx({ collapsed, reducedMotion, stackedLayout = false }) {
  const split = getPendingTasksRailSplitBp(stackedLayout);
  const duration = getRailMotionDuration(reducedMotion);
  const panelWidthLg = collapsed ? 0 : PENDING_TASKS_RAIL_WIDTH.lg;
  const panelWidthXl = collapsed ? 0 : PENDING_TASKS_RAIL_WIDTH.xl;

  if (stackedLayout) {
    return {
      flex: '0 0 auto',
      width: '100%',
      maxWidth: '100%',
      minWidth: '100%',
      minHeight: 0,
      overflow: 'visible',
      display: 'flex',
      flexDirection: 'column',
    };
  }

  return {
    flex: {
      xs: '0 0 auto',
      [split]: `0 0 ${panelWidthLg}px`,
      xl: `0 0 ${panelWidthXl}px`,
    },
    width: {
      xs: '100%',
      [split]: panelWidthLg,
      xl: panelWidthXl,
    },
    maxWidth: {
      xs: '100%',
      [split]: panelWidthLg,
      xl: panelWidthXl,
    },
    minWidth: { xs: '100%', [split]: 0 },
    height: { xs: 'auto', [split]: '100%' },
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    opacity: collapsed ? 0 : 1,
    transform: collapsed ? 'translate3d(14px, 0, 0)' : 'translate3d(0, 0, 0)',
    transition: reducedMotion ? 'none' : RAIL_PANEL_TRANSITION,
    pointerEvents: { [split]: collapsed ? 'none' : 'auto' },
    backfaceVisibility: 'hidden',
  };
}

export function getRailMotionDuration(reducedMotion) {
  return reducedMotion ? 0 : RAIL_TRANSITION_MS;
}

export function getRailLayoutTransition(reducedMotion) {
  if (reducedMotion) return 'none';
  return RAIL_LAYOUT_TRANSITION;
}

export function getPendingTasksRailPanelOuterSx(stackedLayout = false) {
  if (stackedLayout) {
    return null;
  }

  return {
    flex: 1,
    minHeight: 0,
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
}

export function getPendingTasksRailPanelShellSx(stackedLayout = false) {
  if (stackedLayout) {
    return {
      width: '100%',
      minWidth: '100%',
      height: '100%',
      maxHeight: 'inherit',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    };
  }

  const split = getPendingTasksRailSplitBp(stackedLayout);
  return {
    flex: 1,
    minHeight: 0,
    width: {
      [split]: PENDING_TASKS_RAIL_WIDTH.lg,
      xl: PENDING_TASKS_RAIL_WIDTH.xl,
    },
    minWidth: {
      [split]: PENDING_TASKS_RAIL_WIDTH.lg,
      xl: PENDING_TASKS_RAIL_WIDTH.xl,
    },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
}

export function getPendingTasksStackedPanelWrapSx({ collapsed, reducedMotion }) {
  return {
    width: '100%',
    minHeight: 0,
    maxHeight: collapsed ? 0 : PENDING_TASKS_STACKED_MAX_HEIGHT,
    height: collapsed ? 0 : 'auto',
    overflow: 'hidden',
    opacity: collapsed ? 0 : 1,
    transform: collapsed ? 'translate3d(0, 8px, 0)' : 'translate3d(0, 0, 0)',
    transition: reducedMotion ? 'none' : RAIL_PANEL_TRANSITION,
    pointerEvents: collapsed ? 'none' : 'auto',
    flex: collapsed ? '0 0 0' : '0 1 auto',
    display: 'flex',
    flexDirection: 'column',
  };
}

export function getPendingTasksStackedExpandBarSx({ isDark, reducedMotion, visible }) {
  const duration = getRailMotionDuration(reducedMotion);
  return {
    display: visible ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    flexShrink: 0,
    px: 1.5,
    py: 1,
    gap: 1,
    border: 'none',
    cursor: 'pointer',
    borderRadius: 2,
    bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
    color: isDark ? '#e2e8f0' : '#334155',
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
      : 'inset 0 1px 0 rgba(255,255,255,0.7)',
    transition: reducedMotion
      ? 'none'
      : `background-color 220ms ${RAIL_TRANSITION_EASING}, transform 220ms ${RAIL_TRANSITION_EASING}, opacity 280ms ease`,
    opacity: visible ? 1 : 0,
    transform: visible ? 'translate3d(0, 0, 0)' : 'translate3d(0, 6px, 0)',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        bgcolor: isDark ? 'rgba(76,225,182,0.08)' : 'rgba(76,225,182,0.06)',
        borderColor: isDark ? 'rgba(76,225,182,0.22)' : 'rgba(76,225,182,0.18)',
      },
    },
    '&:focus-visible': {
      outline: `2px solid ${isDark ? '#4ce1b6' : '#0f766e'}`,
      outlineOffset: 2,
    },
  };
}

export function getPendingTasksTabShellSx({ top, visible, reducedMotion, stackedLayout = false }) {
  if (stackedLayout) {
    return { display: 'none' };
  }

  const split = getPendingTasksRailSplitBp(stackedLayout);
  const duration = getRailMotionDuration(reducedMotion);
  return {
    display: { xs: 'none', [split]: visible ? 'block' : 'none' },
    position: 'fixed',
    right: 0,
    top,
    zIndex: PENDING_TASKS_TAB_Z_INDEX,
    opacity: visible ? 1 : 0,
    transform: visible ? 'translate3d(0, 0, 0)' : 'translate3d(8px, 0, 0)',
    transition: reducedMotion
      ? 'none'
      : `
        opacity 280ms ease,
        transform 320ms ${RAIL_TRANSITION_EASING},
        top ${duration}ms ${RAIL_TRANSITION_EASING}
      `,
    pointerEvents: visible ? 'auto' : 'none',
  };
}

export function getPendingTasksTabFloatWrapperSx({ visible, reducedMotion }) {
  if (reducedMotion || !visible) {
    return { display: 'block' };
  }

  return {
    display: 'block',
    '@keyframes pendingTasksTabFloat': {
      '0%, 100%': { transform: 'translate3d(0, 0, 0)' },
      '50%': { transform: 'translate3d(0, -7px, 0)' },
    },
    animation: `pendingTasksTabFloat ${PENDING_TASKS_TAB_FLOAT_DURATION_MS}ms ease-in-out infinite`,
    willChange: 'transform',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      transform: 'none',
    },
  };
}

export function getPendingTasksTabButtonSx({ isDark, reducedMotion }) {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    width: 32,
    px: 0.5,
    py: 1.25,
    border: 'none',
    borderRadius: '8px 0 0 8px',
    cursor: 'pointer',
    bgcolor: isDark ? '#3f4654' : '#64748b',
    color: isDark ? '#e2e8f0' : '#f8fafc',
    boxShadow: isDark
      ? 'inset 1px 0 0 rgba(255,255,255,0.08), -4px 0 16px rgba(0,0,0,0.28)'
      : 'inset 1px 0 0 rgba(255,255,255,0.25), -4px 0 16px rgba(15,23,42,0.12)',
    transition: reducedMotion
      ? 'none'
      : `background-color 220ms ${RAIL_TRANSITION_EASING}, transform 220ms ${RAIL_TRANSITION_EASING}`,
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        bgcolor: isDark ? '#4a5568' : '#475569',
        transform: reducedMotion ? 'none' : 'translate3d(-2px, 0, 0)',
      },
    },
    '&:focus-visible': {
      outline: `2px solid ${isDark ? '#4ce1b6' : '#0f766e'}`,
      outlineOffset: 2,
    },
  };
}

export function scheduleAfterRailTransition(callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }

  const run = () => {
    try {
      callback();
    } catch (error) {
      if (!IS_PRODUCTION) {
        console.warn('[pending-tasks-rail] transition callback failed', error);
      }
    }
  };

  if (typeof window === 'undefined') {
    return () => {};
  }

  let rafId = 0;
  let timeoutId = 0;

  rafId = window.requestAnimationFrame(() => {
    timeoutId = window.setTimeout(run, Math.max(0, RAIL_TRANSITION_MS - 24));
  });

  return () => {
    if (rafId) window.cancelAnimationFrame(rafId);
    if (timeoutId) window.clearTimeout(timeoutId);
  };
}

export function readPendingTasksRailCollapsed() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    const key = getPendingTasksCollapsedStorageKey();
    const stored = window.localStorage.getItem(key);
    if (stored === '1') return true;
    if (stored === '0') return false;
    // Tablet/iPad: open by default (independent of desktop collapse preference)
    return false;
  } catch {
    return false;
  }
}

export function writePendingTasksRailCollapsed(collapsed) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(getPendingTasksCollapsedStorageKey(), collapsed ? '1' : '0');
  } catch {
    // ignore storage errors (private mode, quota, disabled storage)
  }
}

export function usePendingTasksTabTop({ enabled, refreshKey = 0 }) {
  const [top, setTop] = useState(FALLBACK_TOP_PX);

  useLayoutEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    let mounted = true;
    let rafId = 0;

    const update = () => {
      if (!mounted) return;

      try {
        const anchor = document.querySelector('[data-dashboard-all-rooms-anchor]');
        if (!anchor) {
          setTop((prev) => (prev === FALLBACK_TOP_PX ? prev : FALLBACK_TOP_PX));
          return;
        }

        const rect = anchor.getBoundingClientRect();
        if (!rect || !Number.isFinite(rect.top)) {
          setTop((prev) => (prev === FALLBACK_TOP_PX ? prev : FALLBACK_TOP_PX));
          return;
        }

        const next = Math.max(120, Math.round(rect.top + TASKS_TAB_OFFSET_PX));
        setTop((prev) => (prev === next ? prev : next));
      } catch {
        setTop((prev) => (prev === FALLBACK_TOP_PX ? prev : FALLBACK_TOP_PX));
      }
    };

    let scrollThrottleTimer = 0;
    const scheduleUpdate = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(update);
    };
    // Room-board scroll is frequent — throttle so we do not thrash React state every frame.
    const scheduleScrollUpdate = () => {
      if (scrollThrottleTimer) return;
      scrollThrottleTimer = window.setTimeout(() => {
        scrollThrottleTimer = 0;
        scheduleUpdate();
      }, 80);
    };

    scheduleUpdate();

    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.addEventListener('scroll', scheduleScrollUpdate, { passive: true, capture: true });

    const calendarRoot = document.querySelector('[data-dashboard-room-board-root]');
    calendarRoot?.addEventListener('scroll', scheduleScrollUpdate, { passive: true });

    let resizeObserver;
    const anchor = document.querySelector('[data-dashboard-all-rooms-anchor]');
    if (anchor && typeof ResizeObserver !== 'undefined') {
      try {
        resizeObserver = new ResizeObserver(scheduleUpdate);
        resizeObserver.observe(anchor);
      } catch {
        resizeObserver = undefined;
      }
    }

    let mutationObserver;
    if (typeof MutationObserver !== 'undefined' && anchor) {
      try {
        mutationObserver = new MutationObserver(scheduleUpdate);
        mutationObserver.observe(anchor, { attributes: true, childList: true });
      } catch {
        mutationObserver = undefined;
      }
    }

    return () => {
      mounted = false;
      if (rafId) window.cancelAnimationFrame(rafId);
      if (scrollThrottleTimer) window.clearTimeout(scrollThrottleTimer);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleScrollUpdate, true);
      calendarRoot?.removeEventListener('scroll', scheduleScrollUpdate);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [enabled, refreshKey]);

  return top;
}
